import { generateClient } from 'aws-amplify/api';
import { createAnalyticsEvent } from '../graphQL/mutations';

const BATCH_INTERVAL = 5000;
const BATCH_SIZE = 10;
const MAX_EVENTS_PER_SESSION = 500;
let eventCount = 0;

function generateId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function getStoredId(getStorage, key) {
  let id = null;
  try {
    id = getStorage().getItem(key);
  } catch {}
  if (!id) {
    id = generateId();
    try {
      getStorage().setItem(key, id);
    } catch {}
  }
  return id;
}

let deviceId = getStoredId(() => window.localStorage, 'spiggl_device_id');

let sessionId = getStoredId(() => window.sessionStorage, 'spiggl_session_id');

let currentUserId = null;
let eventQueue = [];
let flushTimer = null;
let sessionStartTime = Date.now();
let isAuthenticated = !!currentUserId;

function getClient() {
  return generateClient();
}

function startFlushTimer() {
  if (flushTimer) return;
  flushTimer = setInterval(flushEvents, BATCH_INTERVAL);
}

function stopFlushTimer() {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

async function flushEvents() {
  if (eventQueue.length === 0) return;

  const batch = eventQueue.splice(0, eventQueue.length);
  const client = getClient();

  await Promise.allSettled(
    batch.map(event =>
      client.graphql({
        query: createAnalyticsEvent,
        variables: { input: event },
        authMode: isAuthenticated ? 'userPool' : 'iam',
      }).catch(() => {})
    )
  );
}

function enqueue(event) {
  if (eventCount >= MAX_EVENTS_PER_SESSION) return;
  eventCount++;
  eventQueue.push(event);
  if (eventQueue.length >= BATCH_SIZE) {
    flushEvents();
  }
}

function baseEvent(eventType, extra = {}) {
  return {
    eventType,
    timestamp: new Date().toISOString(),
    sessionId,
    deviceId,
    userId: currentUserId,
    platform: 'web',
    ...extra,
    metadata: extra.metadata ? JSON.stringify(extra.metadata) : null,
  };
}

let beforeUnloadHandler = null;

export function initAnalytics() {
  startFlushTimer();
  trackEvent('session_start');

  if (beforeUnloadHandler) {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
  }

  beforeUnloadHandler = () => {
    stopFlushTimer();
    const duration = Math.round((Date.now() - sessionStartTime) / 1000);
    const evt = baseEvent('session_end', { duration });
    eventQueue.push(evt);
    flushEvents();
  };

  window.addEventListener('beforeunload', beforeUnloadHandler);
}

export function trackEvent(eventType, extra = {}) {
  enqueue(baseEvent(eventType, extra));
}

export function trackPageView(path, referrer) {
  enqueue(baseEvent('page_view', { page: path, referrer }));
}

export function trackSearch(metadata) {
  enqueue(baseEvent('search', { metadata }));
}

export function trackClick(metadata) {
  enqueue(baseEvent('click', { metadata }));
}

export function trackScroll(metadata) {
  enqueue(baseEvent('scroll', { metadata }));
}

export function setUserId(userId) {
  currentUserId = userId;
  isAuthenticated = true;
}

export function clearUserId() {
  currentUserId = null;
  isAuthenticated = false;
}

export function getDeviceId() {
  return deviceId;
}

export function getSessionId() {
  return sessionId;
}
